import React, { useEffect, useRef, useState } from 'react';
import { BarChartProps } from '.';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Bar, getElementAtEvent } from 'react-chartjs-2';
import dayjs from 'dayjs';
import id from 'dayjs/locale/id';
import { options } from '../../../views/dashboard_bcld/optionChart';
import LoaderChart from '../../../views/dashboard_bcld/LoaderChart';
import FailedChart from '../../../views/dashboard_bcld/FailedChart';
import FailedIcon from '../../../assets/icon/illustrations_empty-state_magnify.svg';
import { Box, Grid, Group, Stack, Text } from '@mantine/core';
import initialString from '../../../utilities/initialString';
import Card from '../Card';
import { GoDotFill } from 'react-icons/go';

ChartJS.register(
  ChartDataLabels,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const BarChart: React.FC<BarChartProps> = ({
  onClick,
  onLoad,
  data,
  chartLabels,
  chartKeys,
  chartColors,
  emptyMessage = 'No data available yet.',
  loading,
  isError,
  withLegend = false,
  chartLabelPosition = 'top',
  chartHeight,
  chartWidth
}) => {
  const chartRef = useRef();
  const [datasets, setDatasets] = useState([]);

  useEffect(() => {
    if (chartLabels.length > 0 && data && data.length > 0) {
      const newDataset = chartLabels.map((item, index) => {
        return {
          label: item,
          data: data.map((dataItem) => dataItem[chartKeys[index]]),
          backgroundColor: chartColors[index]
        };
      });
      setDatasets(newDataset);
    }
  }, [chartLabels, chartColors, chartKeys, data]);

  const handleClickChart = (event) => {
    if (!onClick) return;
    const element = getElementAtEvent(chartRef.current, event);

    onClick(element);
  };

  if (loading) {
    return <LoaderChart />;
  }

  if (isError) {
    return <FailedChart />;
  }

  if (data && data.length === 0) {
    return <FailedChart icon={FailedIcon} message={emptyMessage} />;
  }

  return (
    <Stack w={'100%'} justify="center">
      {chartLabelPosition === 'top' && (
        <Group mx={'auto'}>
          {chartLabels.map((label, index) => (
            <Group gap={4} key={index}>
              <div
                style={{
                  width: 15,
                  height: 15,
                  backgroundColor: chartColors[index],
                  borderRadius: 30
                }}
              />
              <Text className="text-xs-regular">{label} </Text>
            </Group>
          ))}
        </Group>
      )}
      <Box component="div" h={chartHeight} w={chartWidth}>
        <Bar
          ref={chartRef}
          onClick={handleClickChart}
          data={{
            labels:
              data &&
              data
                ?.sort((a, b) => a?.label - b?.label)
                ?.map((item) => {
                  return item?.label
                    ? withLegend
                      ? item.shortLabel || initialString(item.label)
                      : item.shortLabel || initialString(item.label)
                    : item.month && item.year
                    ? `${dayjs()
                        .month(item.month - 1)
                        .locale(id)
                        .format('MMM')} ${dayjs()
                        .year(item.year)
                        .locale(id)
                        .format('YYYY')}`
                    : `${dayjs()
                        .month(item.bulan - 1)
                        .locale(id)
                        .format('MMM')} ${dayjs()
                        .year(item.tahun)
                        .locale(id)
                        .format('YYYY')}`;
                }),
            datasets
          }}
          options={options({
            xTickRotation: data.length > 10 ? 90 : undefined,
            data: withLegend ? data : undefined
          })}
        />
      </Box>

      {chartLabelPosition === 'bottom' && (
        <Group mx={'auto'}>
          {chartLabels.map((label, index) => (
            <Group gap={4} key={index}>
              <div
                style={{
                  width: 15,
                  height: 15,
                  backgroundColor: chartColors[index],
                  borderRadius: 30
                }}
              />
              <Text className="text-xs-regular">{label} </Text>
            </Group>
          ))}
        </Group>
      )}

      {withLegend && data && data?.length > 0 && data[0]?.label && (
        <Card>
          <Grid gutter={4} mx={'auto'}>
            {data
              ?.sort((a, b) => a?.label - b?.label)
              ?.map((item, index) => (
                <Grid.Col key={index} span={'content'}>
                  <Group gap={4} align="flex-start">
                    <GoDotFill />
                    <Text className="text-xs-semibold">
                      {' '}
                      {item?.shortLabel || initialString(item?.label)} :
                    </Text>
                    <Text className="text-xs-regular" maw={600}>
                      {' '}
                      {item?.label} - (
                      {chartLabels?.map((label, i) => {
                        return `${label} : ${item[chartKeys[i]]}${
                          i === chartLabels.length - 1 ? '' : ', '
                        }`;
                      })}
                      ){' '}
                    </Text>
                  </Group>
                </Grid.Col>
              ))}
          </Grid>
        </Card>
      )}
    </Stack>
  );
};

export default BarChart;
